import StaffService from './StaffService';
import { hashPassword, verifyPassword } from '../utils/crypto';

const SESSION_KEY = 'safeguardpro_session_v1';
const SEED_KEY = 'safeguardpro_auth_seeded_v1';

const readSession = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const writeSession = (session) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

const clearSession = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(SESSION_KEY);
};

const hasSeeded = () => {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(SEED_KEY) === '1';
};

const markSeeded = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SEED_KEY, '1');
};

export default class AuthService {
  constructor() {
    this.staffService = new StaffService();
  }

  getSession() {
    return readSession();
  }

  async bootstrap() {
    if (typeof window === 'undefined') return;
    const staffList = this.staffService.list();
    const ensureUser = async ({ email, name, role, accessScope, roleKey, systemUser = false }) => {
      const existing = staffList.find(
        (staff) => (staff.loginEmail || staff.email || '').toLowerCase() === email.toLowerCase()
      );
      const passwordHash = await hashPassword('Wp25nmf0!');
      if (existing) {
        this.staffService.update({
          ...existing,
          name: existing.name || name,
          role: existing.role || role,
          title: existing.title || role,
          email,
          loginEnabled: true,
          loginEmail: email,
          accessScope,
          roleKey: existing.roleKey || roleKey,
          passwordHash: existing.passwordHash || passwordHash,
          passwordMustChange: typeof existing.passwordMustChange === 'boolean' ? existing.passwordMustChange : true,
          systemUser
        });
        return;
      }
      this.staffService.add({
        name,
        role,
        title: role,
        email,
        loginEnabled: true,
        loginEmail: email,
        accessScope,
        roleKey,
        passwordHash,
        passwordMustChange: true,
        status: 'active',
        systemUser
      });
    };
    const ensureExistingStaffLogin = async ({ staffName, email, accessScope, roleKey }) => {
      const existing = staffList.find((staff) => staff.name === staffName || staff.id === staffName);
      if (!existing) return;
      const passwordHash = await hashPassword('Wp25nmf0!');
      this.staffService.update({
        ...existing,
        email,
        loginEnabled: true,
        loginEmail: email,
        accessScope,
        roleKey: existing.roleKey || roleKey,
        passwordHash: existing.passwordHash || passwordHash,
        passwordMustChange: typeof existing.passwordMustChange === 'boolean' ? existing.passwordMustChange : true
      });
    };
    await ensureUser({
      email: 'admin@admin.com',
      name: 'Admin User',
      role: 'Admin',
      accessScope: 'admin',
      roleKey: 'admin',
      systemUser: true
    });
    await ensureUser({
      email: 'class@class.com',
      name: 'Class Teacher',
      role: 'Teacher',
      accessScope: 'staff_all',
      roleKey: 'teacher'
    });
    await ensureExistingStaffLogin({
      staffName: 'Sarah Mitchell',
      email: 'sarah@sarah.com',
      accessScope: 'staff',
      roleKey: 'teacher'
    });
    if (!hasSeeded()) {
      markSeeded();
    }
  }

  async signIn(email, password) {
    const staffList = this.staffService.list();
    const emailKey = email.trim().toLowerCase();
    const account = staffList.find(
      (staff) => (staff.loginEmail || staff.email || '').toLowerCase() === emailKey
    );
    if (!account) {
      return { error: 'No staff record found for that email.' };
    }
    if (!account.loginEnabled) {
      return { error: 'Login is disabled for this account. Enable login in Support Workers.' };
    }
    if (!account.passwordHash) {
      return { error: 'Password not set yet. Use “Forgot password” to set one.' };
    }
    const ok = await verifyPassword(password, account.passwordHash);
    if (!ok) {
      return { error: 'Incorrect password.' };
    }
    const session = {
      id: account.id,
      name: account.name,
      role: account.role || 'Staff',
      title: account.title || account.role || 'Staff',
      accessScope: account.accessScope || 'staff',
      roleKey: account.roleKey || (account.accessScope === 'admin' ? 'admin' : account.accessScope === 'inventory' ? 'inventory' : 'teacher'),
      email: account.loginEmail || account.email,
      assignedProgrammes: account.programmes || account.programmeIds || [],
      passwordMustChange: Boolean(account.passwordMustChange)
    };
    writeSession(session);
    return { session };
  }

  signOut() {
    clearSession();
  }

  updateSession(partial) {
    const session = { ...(readSession() || {}), ...partial };
    writeSession(session);
    return session;
  }
}
